import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import WelcomeList from "./WelcomeList";
import PrimaryButton from "../PrimaryButton";
import { createConnectedAccount, getOnboardingStatus } from "../../../services/fundraiserService";
import { getFcmToken } from "../../../utils/connectStore";
import { clearAuth } from "../../../redux/slices/authSlice";
import { ReactComponent as DollarIcon } from "../../../Assets/Icons/dollarIcon.svg";
import { ReactComponent as TimeIcon } from "../../../Assets/Icons/timeIcon.svg";
import { ReactComponent as ListIcon } from "../../../Assets/Icons/listIcon.svg";
import Loader from "../Loader";
import axios from "../../../utils/axios";

const FundraiserWelcome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const [isChecking, setIsChecking] = useState(true);

  // Check Stripe onboarding status on mount and redirect if already complete
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // First check if user has a token - if not, they shouldn't be here
      if (!token) {
        setIsChecking(false);
        navigate("/fundraiser/account-login", { replace: true });
        return;
      }

      try {
        const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
        const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
        const chargesEnabled = statusPromise?.data?.chargesEnabled === true;
        
        if (detailsSubmitted && chargesEnabled) {
          // Stripe onboarding is complete - check approval status before redirecting
          // Use explicit token like in Login page to ensure it works correctly
          try {
            const checkResponse = await axios.get("fundraiser/profile", {
              headers: { 
                Authorization: `Bearer ${token}`,
                "api-key": process.env.REACT_APP_API_KEY
              }
            });
            
            const fundraiserProfile = checkResponse?.data?.data;
            const profileStatus = typeof fundraiserProfile?.status === "string" 
              ? fundraiserProfile.status.toLowerCase() 
              : fundraiserProfile?.status;
            const isApproved = profileStatus === "approved";
            
            if (isApproved) {
              // Approved AND Stripe complete - go to dashboard
              navigate("/fundraiser/dashboard", { replace: true });
              return;
            } else {
              // Stripe complete but NOT approved - clear auth and redirect (no logout API call needed)
              dispatch(clearAuth());
              // Use sessionStorage to persist modal flag across page reloads (Stripe redirect causes full page reload)
              sessionStorage.setItem('showAccountPendingModal', 'true');
              navigate("/fundraiser/account-login", { 
                replace: true,
                state: { 
                  message: "Your account setup is complete! Your account is pending admin approval. You will be notified once approved.",
                  showAccountPendingModal: true // Flag to show modal for newly created fundraiser
                }
              });
              return;
            }
          } catch (profileError) {
            console.error("Failed to fetch fundraiser profile:", profileError);
            // If profile fetch fails, clear auth and redirect (no logout API call needed)
            dispatch(clearAuth());
            navigate("/fundraiser/account-login", { 
              replace: true,
              state: { 
                message: "Failed to verify account status. Please try again." 
              }
            });
            return;
          }
        } else if (detailsSubmitted && !chargesEnabled) {
          // Stripe details submitted but not charges enabled, go to complete-setup
          navigate("/complete-setup", { replace: true });
          return;
        }
        // If not complete, stay on welcome page
        setIsChecking(false);
      } catch (error) {
        // If error checking status, stay on welcome page
        setIsChecking(false);
      }
    };
    checkOnboardingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = async () => {
    const linkPromise = await dispatch(createConnectedAccount()).unwrap();
    window.location.href = linkPromise?.data;
  };

  const welcomeList = [
    {
      heading: "How long does it take?",
      text: "The process typically takes 10 minutes. Once completed you will be able to access your Fundraiser Dashboard and set up your Fundraising campaign",
      icon: <TimeIcon />,
    },
    {
      heading: "What does it cost?",
      text: "Absolutely nothing — it’s free to join and free to use, always.",
      icon: <DollarIcon />,
    },
    {
      heading: "Do I need anything before I get started?",
      text: (
        <span>
          Have the following information ready. you'll need this information to complete the next steps: Please add in;
          <ul style={{ marginTop: '8px', paddingLeft: '20px', listStyleType: 'disc', color: '#767676', fontSize: '14px', fontFamily: 'Poppins', fontWeight: 400 }}>
            <li>Email</li>
            <li>Phone Number</li>
            <li>Contact name</li>
            <li>Date of Birth</li>
            <li>Address</li>
            <li>Your industry type</li>
            <li>Website</li>
            <li>BSB</li>
            <li>Account number</li>
            <li>Photo ID</li>
          </ul>
        </span>
      ),
      icon: <ListIcon />,
    },
  ];
  // Show loader while checking onboarding status to prevent flash
  if (isChecking) {
    return <Loader />;
  }

  return (
    <div className="sm:w-[80%] md:w-[70%] lg:w-[60%] py-20">
      <p className="font-poppins font-[500] text-[28px] text-center text-[#303030]">
        Welcome to Swoodle Fundraiser!
      </p>
      <p className="font-poppins font-[400] text-[14px] text-center text-[#767676]">
        Select get started to build your fundraising campaign.
      </p>
      <p className="font-poppins font-[500] text-[18px] text-start text-[#303030] mt-10">
        Account Setup
      </p>
      <p className="font-poppins font-[400] text-[14px] text-start text-[#767676] mt-2">
        Initiate your Swoodle Fundraising campaign effortlessly.
        To receive funds, we’ll guide you through a quick setup of
        your Stripe account. Simply click ‘Get Started’ to begin.
      </p>
      <div className="flex flex-col gap-5 mt-10">
        {welcomeList?.map((item) => (
          <WelcomeList
            heading={item?.heading}
            text={item?.text}
            icon={item?.icon}
          />
        ))}
      </div>
      <div className="w-full text-end mt-20">
        <PrimaryButton
          className={
            "w-[20%] font-montserrat text-[14px] text-center font-[500] h-[45px] rounded-[5px]"
          }
          label="Get Started"
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default FundraiserWelcome;
